import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn("p-3 w-full", className)}
      classNames={{
        months:
          "flex flex-col items-center sm:flex-row sm:justify-center space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-alpine-gold/15 [&:has([aria-selected])]:bg-alpine-gold/15 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md border border-transparent hover:bg-gray-100 hover:text-pine-green hover:border-gray-300 hover:border-solid",
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-alpine-gold/30 text-neutral-900 border border-transparent hover:bg-alpine-gold/35 hover:text-pine-green hover:border-gray-300 hover:border-solid focus:bg-alpine-gold/30 focus:text-neutral-900",
        day_today:
          "bg-transparent text-neutral-900 border-2 border-dotted border-gray-400 rounded-md hover:bg-gray-50 hover:border-gray-500 hover:border-solid hover:text-pine-green",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-alpine-gold/15 aria-selected:text-muted-foreground aria-selected:opacity-40",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-alpine-gold/15 aria-selected:text-neutral-900 border border-transparent hover:border-gray-300 hover:border-solid hover:text-pine-green",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => (
          <ChevronLeft className="h-4 w-4" data-oid="x9lonvl" />
        ),

        IconRight: ({ ..._props }) => (
          <ChevronRight className="h-4 w-4" data-oid="6:i70d7" />
        ),
      }}
      {...props}
      data-oid="h:sqqnw"
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
